import React, { useState } from "react";
import { Button, Spacer, SystemMessage } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { AdSystemT, useCreditsQuery, useLaunchRunwayQuery } from "../../graphql/generated/graphql";
import { useWizardState } from "../../hooks/useWizardState";
import { useAppSettings } from "../../providers/appSettingsProvider";
import { Icon } from "../Icons";
import { ModalWrapper } from "../ModalWrapper";
import { SmallLoader } from "../SmallLoader";
import { CreditOptions, CreditTypes } from "./CreditOptions";
import { getRemainingCreditDays } from "./getRemainingCreditDays";
import { getCurrencyLimits, useCreditPayment } from "./useCreditPayment";

type CreditModalPropsT = {
  isOpen: boolean;
  onClose: () => void;
  startCampaignFor?: AdSystemT;
  useRecommendedMonthlyBudget?: boolean;
};

export const DashboardCreditsModal = ({ isOpen, onClose, startCampaignFor }: CreditModalPropsT) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.Credits" });
  const { currency: defaultCurrency } = useAppSettings();

  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [isCreditValid, setIsCreditValid] = useState<boolean>(true);

  const currency = defaultCurrency;
  const { maximalPaymentAmount, minimalPaymentAmount } = getCurrencyLimits(currency);

  const { data, loading: creditsLoading } = useCreditsQuery();
  const { data: launchRunwayData, loading: launchRunwayLoading } = useLaunchRunwayQuery({
    variables: { adSystem: startCampaignFor || AdSystemT.GoogleAdsT },
    skip: !startCampaignFor,
    fetchPolicy: "network-only",
  });
  const loading = creditsLoading || launchRunwayLoading;
  const { googleWizardFinished, metaWizardFinished } = useWizardState();

  const isFirstPayment = data?.organization?.boughtCredits?.amount === 0;
  const googleBudget = data?.organization?.googleAdsSetting?.monthlyBudget?.amount || 0;
  const metaBudget = data?.organization?.metaAdsSetting?.monthlyBudget?.amount || 0;
  const runningAdSystems = data?.organization?.runningAdSystems || [];
  const finishedAdSystems = [
    ...(googleWizardFinished ? [AdSystemT.GoogleAdsT] : []),
    ...(metaWizardFinished ? [AdSystemT.MetaAdsT] : []),
  ];
  const launchingAdSystems = launchRunwayData?.organization?.runningAdSystems || [];
  const countedAdSystems = startCampaignFor
    ? launchingAdSystems
    : runningAdSystems.length > 0
    ? runningAdSystems
    : finishedAdSystems;
  const monthlyBudget =
    (countedAdSystems.includes(AdSystemT.GoogleAdsT) ? googleBudget : 0) +
    (countedAdSystems.includes(AdSystemT.MetaAdsT) ? metaBudget : 0);
  const monthlyBudgetThreeMonths = monthlyBudget * 3;

  const creditsBalance = data?.organization?.creditBalance?.amount || 0;

  const minimalNextPaymentAmount = minimalPaymentAmount;
  const remainingOriginalDays = startCampaignFor
    ? launchRunwayData?.organization?.remainingCreditDays || 0
    : getRemainingCreditDays(data?.organization);

  const {
    error,
    initiatePaymentForAmount,
    isRedirecting,
    loading: loadingPayment,
  } = useCreditPayment({
    currency,
    startCampaignFor,
  });

  const topUpCredit = () => {
    initiatePaymentForAmount(creditAmount);
  };

  const handleCreditChange = (creditType: CreditTypes, amount: number, isValid: boolean) => {
    setCreditAmount(amount);
    setIsCreditValid(isValid);
  };

  const resetState = () => {
    setCreditAmount(monthlyBudgetThreeMonths);
    setIsCreditValid(true);
  };

  const isTopUpButtonDisabled = loadingPayment || !isCreditValid;

  if (loading || !data?.organization) {
    return <SmallLoader />;
  }

  return (
    <ModalWrapper isOpen={isOpen} title={t("Increase headline")} maxWidth onClose={onClose} onClosed={resetState}>
      {error ? (
        <div>
          <SystemMessage description={t("Unable to initiate payment")} level="error" />
          <Button onClick={onClose}>{t("Back")}</Button>
        </div>
      ) : isRedirecting ? (
        <div>
          <div style={{ justifyContent: "center", alignItems: "center", display: "flex", height: "300px" }}>
            <div style={{ textAlign: "center" }}>
              <Icon kind="loader" />
              <br />
              {t("Redirection to payment gaytway in progress")}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: "8px",
                padding: "16px",
                border: "2px solid var(--app-primary)",
                borderRadius: "var(--app-radius-box)",
              }}
            >
              <Icon kind="info-item" style={{ color: "var(--app-primary)" }} />
              <p style={{ margin: "0" }}>
                <Trans
                  components={{ b: <b />, strong: <strong /> }}
                  t={t}
                  values={{ creditsBalance, days: remainingOriginalDays }}
                  i18nKey={
                    "The current status of your credit is {{ creditsBalance }}. Your credit will last for another {{ days }} days."
                  }
                />
              </p>
            </div>
            <CreditOptions
              currency={currency}
              isFirstPayment={isFirstPayment}
              maximalPaymentAmount={maximalPaymentAmount}
              minimalNextPaymentAmount={minimalNextPaymentAmount}
              monthlyBudget={monthlyBudget}
              monthlyBudgetThreeMonths={monthlyBudgetThreeMonths}
              onCreditChange={handleCreditChange}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={onClose}>
              {t("Back")}
            </Button>
            <Spacer size="sm" />
            <Button
              data-test-id="credit-increase-save-button"
              disabled={isTopUpButtonDisabled}
              variant="primary"
              onClick={topUpCredit}
            >
              {t("Top up credit")}
            </Button>
          </div>
        </>
      )}
    </ModalWrapper>
  );
};
